import React, { useState, useEffect, useRef } from 'react';
import { MarkdownText } from './MarkdownText';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Bot,
  User,
  Trash2,
  Minimize2,
  Maximize2,
  Sparkles,
  Mic
} from 'lucide-react';
import VoiceInput from './VoiceInput';

const LegalChatAssistant = ({ contractId, contractTitle, isMinimized, onToggle }) => {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, contractId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      const params = contractId ? `?contract_id=${contractId}` : '';
      const response = await api.get(`/chat/history${params}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    setLoading(true);

    try {
      const response = await api.post('/chat', {
        message: text,
        contract_id: contractId || null
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        created_at: response.data.timestamp
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un error. Por favor intenta nuevamente.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceTranscription = (text) => {
    if (text) {
      setInput(text);
      // Optionally auto-send
      // sendMessage(text);
    }
  };

  const clearHistory = async () => {
    try {
      const params = contractId ? `?contract_id=${contractId}` : '';
      await api.delete(`/chat/history${params}`);
      setMessages([]);
      toast.success('Historial eliminado');
    } catch (error) {
      toast.error('Error al eliminar historial');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "¿Qué significa esta cláusula?",
    "¿Este contrato tiene riesgos?",
    "¿Qué debo saber antes de firmar?",
    "¿Es legal esta condición?"
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="open-chat-btn"
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform z-50"
        style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)' }}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 bg-card border border-border rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden transition-all ${
        isMinimized ? 'w-80 h-14' : 'w-96 h-[500px]'
      }`}
      data-testid="chat-assistant-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-sm">Asesor NotaryOS</div>
            <div className="text-xs text-blue-100">Tu abogado IA 24/7</div>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={onToggle || (() => {})}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title={isMinimized ? 'Expandir' : 'Minimizar'}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="close-chat-btn"
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Contract Context */}
          {contractTitle && (
            <div className="px-3 py-2 bg-blue-500/10 border-b border-border">
              <div className="text-xs text-slate-400">Analizando:</div>
              <div className="text-sm text-blue-400 truncate">{contractTitle}</div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Sparkles className="w-12 h-12 mx-auto text-blue-400 mb-3" />
                <h3 className="font-medium mb-2">¡Hola! Soy tu asesor legal</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Pregúntame sobre contratos, cláusulas o cualquier duda legal.
                </p>
                <div className="space-y-2">
                  {suggestedQuestions.map((question, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(question)}
                      className="block w-full text-left text-sm px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${
                  msg.role === 'user' 
                    ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm' 
                    : 'bg-slate-800 text-slate-200 rounded-2xl rounded-bl-sm'
                } px-4 py-2`}>
                  <div className="flex items-start space-x-2">
                    {msg.role === 'assistant' && (
                      <Bot className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    )}
                    {msg.role === 'assistant'
                      ? <MarkdownText className="text-sm">{msg.content}</MarkdownText>
                      : <div className="text-sm whitespace-pre-wrap">{msg.content}</div>}
                    {msg.role === 'user' && (
                      <User className="w-4 h-4 text-blue-200 mt-0.5 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                    <span className="text-sm text-slate-400">Pensando...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border">
            <div className="flex items-end space-x-2">
              <button
                onClick={clearHistory}
                className="p-2 text-slate-500 hover:text-slate-300 transition-colors"
                title="Limpiar historial"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <VoiceInput 
                onTranscription={handleVoiceTranscription}
                disabled={loading}
              />
              <div className="flex-1 relative">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  data-testid="chat-input"
                  placeholder="Escribe o habla tu pregunta..."
                  rows={1}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none text-sm"
                  style={{ minHeight: '40px', maxHeight: '100px' }}
                />
              </div>
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                data-testid="send-message-btn"
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LegalChatAssistant;
